import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { apiClient, auth, formatMoney, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { toast } from "sonner";
import { Upload, ArrowLeft, Download, FileText, Eye, EyeOff, Trash2 } from "lucide-react";

export default function ReferralDetail({ adminView = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = auth.getRole();
  const canEdit = role === "admin" || role === "employee";
  const backPath =
    role === "admin" ? "/admin/dashboard" :
    role === "employee" ? "/employee/dashboard" :
    "/partner/dashboard";
  const loginPath =
    role === "admin" ? "/admin/login" :
    role === "employee" ? "/employee/login" :
    "/partner/login";
  const [referral, setReferral] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({
    status: "new_lead",
    funding_amount: "",
    commission_amount: "",
    commission_paid: false,
    internal_notes: "",
    partner_notes: "",
  });
  const [upFile, setUpFile] = useState(null);
  const [upVisibility, setUpVisibility] = useState(adminView ? "partner" : "partner");

  const load = useCallback(async () => {
    try {
      const [r, f] = await Promise.all([
        apiClient.get(`/referrals/${id}`),
        apiClient.get(`/referrals/${id}/files`),
      ]);
      setReferral(r.data);
      setFiles(f.data);
      setEdit({
        status: r.data.status,
        funding_amount: r.data.funding_amount ?? "",
        commission_amount: r.data.commission_amount ?? "",
        commission_paid: !!r.data.commission_paid,
        internal_notes: r.data.internal_notes || "",
        partner_notes: r.data.partner_notes || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load referral");
      if (err?.response?.status === 401) navigate(loginPath);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, adminView, loginPath]);

  useEffect(() => {
    if (!role) {
      navigate(loginPath);
      return;
    }
    load();
  }, [load, role, navigate, adminView, loginPath]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: edit.status,
        internal_notes: edit.internal_notes,
        partner_notes: edit.partner_notes,
        funding_amount: edit.funding_amount === "" ? null : Number(edit.funding_amount),
        commission_amount: edit.commission_amount === "" ? null : Number(edit.commission_amount),
        commission_paid: edit.commission_paid,
      };
      await apiClient.patch(`/referrals/${id}`, payload);
      toast.success("Referral updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async () => {
    if (!upFile) return toast.error("Choose a file to upload");
    const fd = new FormData();
    fd.append("file", upFile);
    fd.append("visibility", upVisibility);
    try {
      await apiClient.post(`/referrals/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      setUpFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await apiClient.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const deleteReferral = async () => {
    if (!referral) return;
    if (!window.confirm(`Delete referral for "${referral.business_name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/referrals/${id}`);
      toast.success("Referral deleted");
      navigate(backPath);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return (
      <DashboardShell role={role || "partner"} title="Referral">
        <div className="p-12 text-center text-slate-500">Loading…</div>
      </DashboardShell>
    );
  }
  if (!referral) return null;

  return (
    <DashboardShell
      role={role || "partner"}
      title={referral.business_name}
      subtitle={`Submitted ${new Date(referral.created_at).toLocaleDateString()} • Owner: ${referral.business_owner_name}`}
      actions={
        <div className="flex flex-wrap gap-2">
          {role === "admin" ? (
            <Button
              variant="outline"
              onClick={deleteReferral}
              data-testid={TIDS.detailDelete}
              className="rounded-full text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          ) : null}
          <Link to={backPath}>
            <Button variant="outline" className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          </Link>
        </div>
      }
    >
      <div data-testid={TIDS.referralDetail} className="grid lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-xl border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-medium text-slate-900">Business details</h2>
                <p className="text-sm text-slate-500">Submitted by {referral.partner_snapshot?.full_name || "—"}</p>
              </div>
              <StatusPill status={referral.status} />
            </div>
            <Separator className="my-5" />
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              <Detail label="Owner" value={referral.business_owner_name} />
              <Detail label="Business" value={referral.business_name} />
              <Detail label="Owner phone" value={referral.owner_phone} />
              <Detail label="Owner email" value={referral.owner_email} />
              <Detail label="Monthly revenue" value={formatMoney(referral.monthly_revenue)} />
              <Detail label="Funding needed" value={formatMoney(referral.funding_amount_needed)} />
              <Detail label="Time in business" value={referral.time_in_business} />
              <Detail label="Estimated commission" value={formatMoney(referral.estimated_commission)} />
            </dl>
            {referral.notes ? (
              <>
                <Separator className="my-5" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Partner notes (at submission)</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{referral.notes}</p>
                </div>
              </>
            ) : null}
          </Card>

          {/* Partner-visible note from admin */}
          {referral.partner_notes ? (
            <Card className="rounded-xl border-blue-200 bg-blue-50/40 shadow-sm p-6">
              <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-2">Note from the team</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{referral.partner_notes}</p>
            </Card>
          ) : null}

          {/* Files */}
          <Card className="rounded-xl border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-slate-900">Documents</h2>
              <span className="text-sm text-slate-500">{files.length} file{files.length === 1 ? "" : "s"}</span>
            </div>
            {files.length === 0 ? (
              <p className="text-sm text-slate-500">No documents uploaded yet.</p>
            ) : (
              <ul className="space-y-2">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 hover:border-blue-300 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-700"><FileText className="h-5 w-5" /></span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{f.original_filename}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          {Math.round((f.size || 0) / 1024)} KB • by {f.uploaded_by_role}
                          {f.visibility === "internal" ? (
                            <span className="inline-flex items-center gap-1 text-amber-700"><EyeOff className="h-3 w-3" /> internal</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-700"><Eye className="h-3 w-3" /> shared</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => downloadFile(f)} className="hover:text-blue-700">
                      <Download className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <Separator className="my-5" />
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-2 space-y-2">
                <Label>Upload a new document</Label>
                <Input type="file" data-testid={TIDS.detailUploadFile} onChange={(e) => setUpFile(e.target.files?.[0] || null)} />
              </div>
              {adminView ? (
                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <Select value={upVisibility} onValueChange={setUpVisibility}>
                    <SelectTrigger data-testid={TIDS.detailUploadVisibility}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partner">Shared with partner</SelectItem>
                      <SelectItem value="internal">Internal only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : canEdit ? (
                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <Select value={upVisibility} onValueChange={setUpVisibility}>
                    <SelectTrigger data-testid={TIDS.detailUploadVisibility}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partner">Shared with partner</SelectItem>
                      <SelectItem value="internal">Internal only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : <div />}
              <div className="sm:col-span-3 flex justify-end">
                <Button onClick={uploadFile} disabled={!upFile} data-testid={TIDS.detailUploadButton} className="rounded-full bg-blue-600 hover:bg-blue-700">
                  <Upload className="h-4 w-4 mr-1" /> Upload
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Right: admin/employee edit / partner summary */}
        <div className="space-y-6">
          {canEdit ? (
            <Card className="rounded-xl border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-lg font-medium text-slate-900">Manage referral</h2>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger data-testid={TIDS.detailStatusSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Funding amount</Label>
                  <Input
                    type="number" min="0"
                    data-testid={TIDS.detailFundingAmount}
                    value={edit.funding_amount}
                    onChange={(e) => setEdit({ ...edit, funding_amount: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Commission</Label>
                  <Input
                    type="number" min="0"
                    data-testid={TIDS.detailCommissionAmount}
                    value={edit.commission_amount}
                    onChange={(e) => setEdit({ ...edit, commission_amount: e.target.value })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  data-testid={TIDS.detailCommissionPaid}
                  checked={edit.commission_paid}
                  onCheckedChange={(v) => setEdit({ ...edit, commission_paid: !!v })}
                />
                <span className="text-sm text-slate-700">Mark commission as paid</span>
              </label>
              <div className="space-y-2">
                <Label>Internal notes <span className="text-xs text-slate-500">(team only)</span></Label>
                <Textarea
                  rows={3}
                  data-testid={TIDS.detailInternalNotes}
                  value={edit.internal_notes}
                  onChange={(e) => setEdit({ ...edit, internal_notes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Note to partner <span className="text-xs text-slate-500">(visible to partner)</span></Label>
                <Textarea
                  rows={3}
                  data-testid={TIDS.detailPartnerNotes}
                  value={edit.partner_notes}
                  onChange={(e) => setEdit({ ...edit, partner_notes: e.target.value })}
                />
              </div>
              <Button
                onClick={save}
                disabled={saving}
                data-testid={TIDS.detailSave}
                className="w-full rounded-full bg-blue-600 hover:bg-blue-700"
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </Card>
          ) : (
            <Card className="rounded-xl border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-lg font-medium text-slate-900">Commission</h2>
              <SummaryRow label="Funded amount" value={referral.funding_amount ? formatMoney(referral.funding_amount) : "—"} />
              <SummaryRow label="Commission" value={referral.commission_amount ? formatMoney(referral.commission_amount) : `${formatMoney(referral.estimated_commission)} est.`} />
              <SummaryRow label="Status" value={referral.commission_paid ? <span className="text-emerald-700 font-medium">Paid</span> : <span className="text-slate-700">Unpaid</span>} />
            </Card>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
    <dd className="mt-1 text-sm text-slate-900 font-medium">{value || "—"}</dd>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium">{value}</span>
  </div>
);
